// File: PictureItem.jsx
import React, {useEffect, useRef, useState} from 'react';
import 'styles/pictures/display/PictureItem.css';
import {useNavigate} from 'react-router-dom';
import {useImageCache} from 'components/cacheable/ImageCacheContext';
import PriceTag from 'components/pictures/PriceTag';
import ImageDisplay from 'components/pictures/display/ImageDisplay';
import ImageOverlay from 'components/pictures/display/ImageOverlay';
import {useSessionConfiguration} from 'util/SessionConfigurationContext';
import Badge from "../Badge";

const placeholderProfileImage = '/images/emptyProfile.svg';
const placeholderImage = '/images/GreenLoadingPixel.png';


const PictureItem = ({
                         pic,
                         index = null,
                         onImageLoad,
                         onPictureClick = () => {
                         },
                         listType,
                         navigationClick,
                         canShowNext = false,
                         canShowPrevious = false,
                         isDetail = false
                     }) => {
    const [imageBlob, setImageBlob] = useState(null);
    const [authorImage, setAuthorImage] = useState(placeholderImage);
    const navigate = useNavigate();
    const {imageGeto, profileGeto} = useImageCache();
    const [priceTagWidth, setPriceTagWidth] = useState(100);
    const parentRef = useRef(null);
    const profileContainerRef = useRef(null);
    const [extraPadding, setExtraPadding] = useState(false);
    const [leaveSpaceForPriceTag, setLeaveSpaceForPriceTag] = useState();

    const aspectRatio = pic.width && pic.height ? pic.height / pic.width : 1;
    const paddingTopPercent = Math.min(aspectRatio * 100, 80);
    const [badgeScale, setBadgeScale] = useState(1);
    const {isDesktopView} = useSessionConfiguration();

    useEffect(() => {
        if (index || index === 0) {
            let localIndex = index;
            if (index > 3) {
                localIndex = 3;
            }
            setBadgeScale(1.1 + (0.15 * (3 - localIndex)));
        }
    }, [index]);

    useEffect(() => {
        const parentElem = parentRef.current;
        const profileElem = profileContainerRef.current;
        if (!parentElem || !profileElem) return;

        const observer = new ResizeObserver(() => {
            const parentWidth = parentElem.offsetWidth || 0;
            const profileWidth = profileElem.offsetWidth || 0;
            const isBig = (profileWidth + priceTagWidth) > parentWidth;
            setExtraPadding(isBig);
        });

        observer.observe(parentElem);
        observer.observe(profileElem);

        return () => {
            observer.disconnect();
        };
    }, [priceTagWidth, leaveSpaceForPriceTag]);

    useEffect(() => {
        let isMounted = true;

        // Fetch the main image
        const fetchImageBlob = async () => {
            try {
                setImageBlob(null);
                const blob = await imageGeto.fetchBlob(pic.id);
                if (isMounted) {
                    setImageBlob(URL.createObjectURL(blob));
                }
            } catch (error) {
                console.error(
                    `Error fetching image for picture ID ${pic.id}:`,
                    error
                );
            } finally {
                if (isMounted && onImageLoad) {
                    onImageLoad(pic.id);
                }
            }
        };

        // Fetch the author image
        const fetchAuthorImage = async () => {
            if (pic.authorProfileImgId) {
                try {
                    const blob = await profileGeto.fetchBlob(
                        pic.authorProfileImgId
                    );
                    if (isMounted) {
                        setAuthorImage(URL.createObjectURL(blob));
                    }
                } catch (error) {
                    console.error(
                        `Error fetching author image for author ID ${pic.authorId}:`,
                        error
                    );
                }
            } else {
                if (isMounted) {
                    setAuthorImage(placeholderProfileImage);
                }
            }
        };

        fetchImageBlob();
        fetchAuthorImage();

        return () => {
            isMounted = false; // Clean up to prevent state updates after unmounting
        };
    }, [pic]);

    const handleAuthorClick = () => {
        navigate(`/profile/${pic.authorId}`);
    };

    const handlePictureClick = () => {
        onPictureClick(pic.id);
    };

    const priceTagWidthChange = (width, heightSpace) => {
        setPriceTagWidth(width);
        setLeaveSpaceForPriceTag(heightSpace);
    };

    return (
        <div className="position-relative" ref={parentRef}>
            <div
                className={`profile-container-in-list ${isDetail ? '' : 'main-content-side-body-margin'}`}
                style={{paddingTop: extraPadding ? `${leaveSpaceForPriceTag}px` : '0'}}
                ref={profileContainerRef}>
                <img
                    src={authorImage || placeholderProfileImage}
                    alt={`Author`}
                    className="profile-image-in-list cursor-pointer"
                    onClick={handleAuthorClick}
                />
                <p className="cursor-pointer" onClick={handleAuthorClick}>
                    {pic.authorName}
                </p>

                <Badge type={listType.toLowerCase()} index={index} label={pic.trending} scale={badgeScale}/>
            </div>
            <PriceTag amount={pic.getAmount()} pictureId={pic.id} onWidthChange={priceTagWidthChange}/>

            <div className={`position-relative ${isDesktopView && !isDetail ? 'main-content-side-body-margin' : ''}`}>
                <ImageOverlay canShowPrevious={canShowPrevious} canShowNext={canShowNext} clicked={navigationClick}/>
                <ImageDisplay
                    imageBlob={imageBlob}
                    pic={pic}
                    paddingTopPercent={paddingTopPercent}
                    onPictureClick={handlePictureClick}
                />
            </div>
            {!isDetail &&
                <div className={`image-comment-count main-content-side-body-margin`}>
                    <img src="/images/fatcomments.svg" alt={'comments'} height={'22px'}/>
                    {pic.commentCount &&
                        <div style={{color: '#272727', fontSize: '14px'}}>
                            {pic.commentCount}
                        </div>
                    }
                </div>
            }
        </div>
    );
};

export default PictureItem;
